import {CompanyEntity, CustomFieldProfileCatalogItem, EntityType} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {CustomFieldProfileFormProvider, useCustomFieldProfileForm} from "./useCustomFieldProfileForm";
import {CustomFieldProfileFormFields} from "./CustomFieldProfileFormFields";

export interface CustomFieldProfileFormProps {
  options?: CustomFieldProfileFormOptions;
  defaultValues?: Partial<CustomFieldProfileFormValues>;
  onSubmit?: (values: CustomFieldProfileFormValues) => void;
  submitting?: boolean;
}

export interface CustomFieldProfileFormOptions {
  customFieldProfileId?: string
  regionId?: string;
  companySelectDisabled?: boolean
}

export interface CustomFieldProfileFormValues {
  company: CompanyEntity | string
  objectType: EntityType | string
  name: string
  description: string
  effectiveFromDate: Date
  effectiveToDate?: Date
  catalog?: CustomFieldProfileCatalogItem[]
}

export interface CustomFieldProfileFormDefaultValues extends Partial<CustomFieldProfileFormValues> {

}

export interface CustomFieldProfileFormRawValues {
  company?: string
  objectType?: string
  name?: string
  description?: string
  effectiveFromDate?: Date
  effectiveToDate?: Date
}

export type CustomFieldProfileFormType = UseFormReturnType<CustomFieldProfileFormRawValues, (values: CustomFieldProfileFormRawValues) => CustomFieldProfileFormRawValues>

export const CustomFieldProfileForm = (props: CustomFieldProfileFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const { form, catalog, setCatalog } = useCustomFieldProfileForm({
    defaultValues: defaultValues,
    options: options,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        company: values.company,
        objectType: values.objectType,
        name: values.name,
        description: values.description,
        effectiveFromDate: values.effectiveFromDate,
        effectiveToDate: values.effectiveToDate,
        catalog: catalog
      });
    }
  }, [form, onSubmit, catalog]);

  return (
    <CustomFieldProfileFormProvider form={form}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <CustomFieldProfileFormFields
          options={options}
          catalog={catalog}
          setCatalog={setCatalog}/>

        <Divider my="sm" variant="dotted"/>
        <Button type="submit" loading={submitting} fullWidth mt="xl">
          {t("common.action.submit", {ns: "common"})}
        </Button>
      </form>
    </CustomFieldProfileFormProvider>
  );
};

interface CustomFieldProfileFormModalProps extends CustomFieldProfileFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const CustomFieldProfileFormModal = (props: CustomFieldProfileFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <CustomFieldProfileForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitting={submitting}/>
    </Modal>
  );
};

